import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BookOpenIcon, ClockIcon, SearchIcon, XIcon } from 'lucide-react';
import { programDomains, programTemplates, programsIndexSlug } from '../data/programTemplates';
import { degreeInterestOptions, focusOptions } from '../data/degrees';
import { degreeProgramCombos } from '../utils/degreeSlugs';
import { usePageMeta } from '../hooks/usePageMeta';
import { LegalFooter } from '../components/LegalFooter';
const ANY = '';

/**
 * Every hand-written page under `/program-information/`, built from the same
 * data the pages render from so a new program cannot go unlisted. The
 * generated degree × program combinations are listed separately.
 */
const allTemplatePages: {
  path: string;
  label: string;
}[] = [{
  path: `/program-information/${programsIndexSlug}`,
  label: 'Browse programs by field'
}, ...programTemplates.map(program => ({
  path: `/program-information/${program.slug}`,
  label: `${program.name} (${program.credential})`
}))];

/**
 * Searchable index of every /program-information/ page.
 *
 * Programs are filterable by credential and workforce domain, and searchable
 * across name, hero copy, features, and career roles — so someone searching
 * "phlebotomy" or "ICD-10" lands on the right program even when that term is
 * not in the title.
 */
export function ProgramInformationIndexPage() {
  usePageMeta('Online Program Information | Online College Guide', 'Search accredited online degree and diploma programs by credential and field. Compare length, credits, and career outcomes for each program.');
  const [query, setQuery] = useState('');
  const [credential, setCredential] = useState(ANY);
  const [domain, setDomain] = useState(ANY);
  const credentials = useMemo(() => Array.from(new Set(programTemplates.map(p => p.credential))).sort(), []);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return programTemplates.filter(program => {
      if (credential && program.credential !== credential) return false;
      if (domain && program.domainSlug !== domain) return false;
      if (!q) return true;
      return [program.name, program.credential, program.heroSubhead, program.metaDescription, ...program.features, ...program.careerRoles, ...(program.concentrations?.map(c => c.name) ?? [])].join(' ').toLowerCase().includes(q);
    });
  }, [query, credential, domain]);

  // Generated degree × program combinations, searched separately from the
  // curated programs above — there are several hundred of them.
  const [comboQuery, setComboQuery] = useState('');
  const [comboDegree, setComboDegree] = useState(ANY);
  const [comboArea, setComboArea] = useState(ANY);
  const [comboLimit, setComboLimit] = useState(60);
  const comboResults = useMemo(() => {
    const q = comboQuery.trim().toLowerCase();
    return degreeProgramCombos.filter(combo => {
      if (comboDegree && combo.degreeInterest !== comboDegree) return false;
      if (comboArea && combo.areaStudy !== comboArea) return false;
      if (!q) return true;
      return `${combo.programLabel} ${combo.areaLabel} ${combo.degreeLabel}`.toLowerCase().includes(q);
    });
  }, [comboQuery, comboDegree, comboArea]);

  // A narrowed search should start from the top, not mid-way down a list the
  // visitor already expanded.
  useEffect(() => {
    setComboLimit(60);
  }, [comboQuery, comboDegree, comboArea]);
  const isFiltered = Boolean(query.trim() || credential || domain);
  const clearFilters = () => {
    setQuery('');
    setCredential(ANY);
    setDomain(ANY);
  };
  const domainName = (slug: string) => programDomains.find(d => d.slug === slug)?.name ?? '';
  const selectClass = 'w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors';
  return <main className="bg-white" data-id="element-2497" data-mp="65zji5qs177k">
      {/* Hero */}
      <header className="hero-bg" data-id="element-2498" data-mp="azlxq2cl9j5h">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2499" data-mp="b0ndul8govu2">
          <nav aria-label="Breadcrumb" className="mb-6" data-id="element-2500" data-mp="3sglqge2jdj9z">
            <ol className="flex flex-wrap items-center gap-2 text-xs text-white/70" data-id="element-2501" data-mp="187vhmt9658n">
              <li data-id="element-2502" data-mp="oyldeki4inpi">
                <Link to="/" className="hover:text-white transition-colors" data-id="element-2503" data-mp="oy27y39ntrsd">
                  Home
                </Link>
              </li>
              <li aria-hidden="true" data-id="element-2504" data-mp="twn5fuxj1xd4">/</li>
              <li className="text-white/90" data-id="element-2505" data-mp="f0npxz838uxy">Program Information</li>
            </ol>
          </nav>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-5 max-w-3xl" data-id="element-2506" data-mp="jypkphevmip9">
            Program Information
          </h1>
          <p className="text-lg text-white/80 leading-relaxed max-w-2xl" data-id="element-2507" data-mp="2zp23cj2tjl2p">
            Detailed breakdowns of what each program covers, how long it takes,
            and where it leads — so you can compare before you commit.
          </p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14" data-id="element-2508" data-mp="1hroxju5ygq5u">
        {/* Search + filters */}
        <div className="mb-10" data-id="element-2509" data-mp="1vr8087fzz9ar">
          <label htmlFor="program-search" className="sr-only" data-id="element-2510" data-mp="1vq6sabua32x9">
            Search programs
          </label>
          <div className="relative max-w-xl mb-4" data-id="element-2511" data-mp="141d1xla5w99y">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" aria-hidden="true" data-id="element-2512" data-mp="1424z30jeqf2c" />
            <input id="program-search" type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search programs — try “coding”, “patient care”, or “claims”" className="w-full pl-10 pr-4 py-3 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-2513" data-mp="1ctx7z6ss9g4t" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 max-w-xl" data-id="element-2514" data-mp="2kflx0rvl9d54">
            <div data-id="element-2515" data-mp="2kdsqdckz6sx4">
              <label htmlFor="filter-credential" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2516" data-mp="2kd979sjl9yhi">
                Credential
              </label>
              <select id="filter-credential" value={credential} onChange={e => setCredential(e.target.value)} className={selectClass} data-id="element-2517" data-mp="2u79napgeso50">
                <option value={ANY} data-id="element-2518" data-mp="26g44c1f69fqh">All credentials</option>
                {credentials.map(c => <option key={c} value={c} data-id="element-2519" data-mp="2bfgqejy7yis0">
                    {c}
                  </option>)}
              </select>
            </div>
            <div data-id="element-2520" data-mp="3huq3wuubaug4">
              <label htmlFor="filter-domain" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2521" data-mp="3hxc7kje30pt2">
                Field
              </label>
              <select id="filter-domain" value={domain} onChange={e => setDomain(e.target.value)} className={selectClass} data-id="element-2522" data-mp="1b8jz5jftu7k0">
                <option value={ANY} data-id="element-2523" data-mp="whumsdqmqmek">All fields</option>
                {programDomains.map(d => <option key={d.slug} value={d.slug} data-id="element-2524" data-mp="rirrhl0gbujs">
                    {d.name}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-500" data-id="element-2525" data-mp="nj950kszfbeu">
            <span aria-live="polite" data-id="element-2526" data-mp="iilfk3kej2mq">
              {results.length} {results.length === 1 ? 'program' : 'programs'}
            </span>
            {isFiltered && <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-navy font-medium hover:underline" data-id="element-2527" data-mp="2cwcqd6egvsjr">
                <XIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2528" data-mp="1gsp0hz5bgyqr" />
                Clear filters
              </button>}
            <Link to={`/program-information/${programsIndexSlug}`} className="text-navy font-medium hover:underline" data-id="element-2529" data-mp="1lpywxyr4iao3">
              Browse by field
            </Link>
          </div>
        </div>

        {/* Results */}
        {results.length === 0 ? <div className="border border-gray-200 rounded-xl py-16 px-6 text-center" data-id="element-2530" data-mp="xzuwu1d58t1u">
            <h2 className="text-lg font-bold text-navy mb-2" data-id="element-2531" data-mp="131u1dekmdw0d">
              No programs match that search
            </h2>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto" data-id="element-2532" data-mp="o530t1z7me96">
              Try a broader term, or tell us your goals and we'll suggest
              programs that fit.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3" data-id="element-2533" data-mp="2iewtnmifoinw">
              <button type="button" onClick={clearFilters} className="px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2534" data-mp="2nfkjw5yu8vh2">
                Show all programs
              </button>
              <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-2.5 px-6 rounded-lg text-sm transition-colors duration-200" data-id="element-2535" data-mp="ljv85n0nq2sf">
                Get Matched Now
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2536" data-mp="qgve7xxib5jj" />
              </Link>
            </div>
          </div> : <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2537" data-mp="14f234okh9v72">
            {results.map(program => <li key={program.slug} data-id="element-2538" data-mp="3689h9uocu6rd">
                <Link to={`/program-information/${program.slug}`} className="group flex flex-col h-full border border-gray-200 rounded-xl p-6 hover:border-navy hover:shadow-md transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2539" data-mp="369b5l0udspr1">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-crimson mb-2" data-id="element-2540" data-mp="1hxw8gt48srgb">
                    {program.credential}
                  </span>
                  <h2 className="text-lg font-bold text-navy leading-snug mb-2 group-hover:underline" data-id="element-2541" data-mp="18wg00qwzmzeq">
                    {program.name}
                  </h2>
                  <p className="text-sm text-gray-600 leading-relaxed line-clamp-3 mb-5" data-id="element-2542" data-mp="20lj5ga0m2fsy">
                    {program.heroSubhead}
                  </p>
                  <dl className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-gray-500 mb-5" data-id="element-2543" data-mp="1vt8370ieehx8">
                    <div className="flex items-center gap-1.5" data-id="element-2544" data-mp="1rth7qjhxfdmd">
                      <ClockIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2545" data-mp="2ocnfzfnvfajb" />
                      <dt className="sr-only" data-id="element-2546" data-mp="2jkdj1foc9a7j">Length</dt>
                      <dd data-id="element-2547" data-mp="2jjluupx2mera">{program.weeks} weeks</dd>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-2548" data-mp="63kgg75tumew">
                      <BookOpenIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2549" data-mp="b0u3jg5qulzk" />
                      <dt className="sr-only" data-id="element-2550" data-mp="f1mfwi9wx702">Credits</dt>
                      <dd data-id="element-2551" data-mp="k2aepq4dus4u">{program.credits} credits</dd>
                    </div>
                  </dl>
                  <span className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between gap-3" data-id="element-2552" data-mp="p1wqdv4kc0bj">
                    <span className="text-xs text-gray-500" data-id="element-2553" data-mp="tywyscovxf5t">
                      {domainName(program.domainSlug)}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-navy" aria-hidden="true" data-id="element-2554" data-mp="ytapw2wil04o" />
                  </span>
                </Link>
              </li>)}
          </ul>}
      </div>

      {/* Every degree level × program combination. Separate from the curated
          programs above, because these are generated pages and there are
          several hundred of them. */}
      <section aria-labelledby="combos-heading" className="border-t border-gray-200 bg-light" data-id="element-2555" data-mp="3ikfdwz3ma117">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2556" data-mp="3drmbihvmf4sa">
          <h2 id="combos-heading" className="text-2xl font-bold text-navy mb-2" data-id="element-2557" data-mp="3rj0rsgy58rvn">
            All degree and program combinations
          </h2>
          <p className="text-sm text-gray-600 max-w-2xl mb-6" data-id="element-2558" data-mp="5ddpjsnltc93">
            Every degree level paired with every field of study, each with its
            own page and match form.
          </p>

          <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mb-5" data-id="element-2559" data-mp="273p475zzir2u">
            <div data-id="element-2560" data-mp="225duqwdlqil5">
              <label htmlFor="combo-search" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2561" data-mp="225xcabzcb4yn">
                Search
              </label>
              <input id="combo-search" type="search" value={comboQuery} onChange={e => setComboQuery(e.target.value)} placeholder="e.g. nursing" className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-crimson focus:border-crimson transition-colors" data-id="element-2562" data-mp="mmzgr6cjiq0l" />
            </div>
            <div data-id="element-2563" data-mp="3x9n4mkikaa9">
              <label htmlFor="combo-degree" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2564" data-mp="3tls1izf7ftf">
                Degree level
              </label>
              <select id="combo-degree" value={comboDegree} onChange={e => setComboDegree(e.target.value)} className={selectClass} data-id="element-2565" data-mp="3q4zpwotu11fs">
                <option value={ANY} data-id="element-2566" data-mp="3keofebfemgfj">All levels</option>
                {degreeInterestOptions.map(d => <option key={d.value} value={d.value} data-id="element-2567" data-mp="3gctfnbd4gb3v">
                    {d.type}
                  </option>)}
              </select>
            </div>
            <div data-id="element-2568" data-mp="3bg2zko1bhfig">
              <label htmlFor="combo-area" className="block text-xs font-semibold text-gray-600 mb-1.5" data-id="element-2569" data-mp="36ffai7wnf8qv">
                Area of study
              </label>
              <select id="combo-area" value={comboArea} onChange={e => setComboArea(e.target.value)} className={selectClass} data-id="element-2570" data-mp="2wplzb80f4vn5">
                <option value={ANY} data-id="element-2571" data-mp="2np7bo1zzf4gh">All areas</option>
                {focusOptions.map(f => <option key={f.value} value={f.value} data-id="element-2572" data-mp="2iq44aawj4m5c">
                    {f.type}
                  </option>)}
              </select>
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-5" aria-live="polite" data-id="element-2573" data-mp="23mtaasw124nr">
            {comboResults.length.toLocaleString()}{' '}
            {comboResults.length === 1 ? 'combination' : 'combinations'}
          </p>

          {comboResults.length === 0 ? <p className="text-sm text-gray-600" data-id="element-2574" data-mp="2dlihnrqas6pr">
              No combinations match that search.
            </p> : <>
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2575" data-mp="1kwld0rf0e8fh">
                {comboResults.slice(0, comboLimit).map(item => <li key={item.slug} data-id="element-2576" data-mp="1pvfsbjdrpeuu">
                    <Link to={`/program-information/${item.slug}`} className="group flex items-center justify-between gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3 hover:border-navy hover:shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-crimson" data-id="element-2577" data-mp="1uuiyj75hhr4e">
                      <span className="min-w-0" data-id="element-2578" data-mp="172bzg08dj61a">
                        <span className="block text-sm font-semibold text-navy truncate" data-id="element-2579" data-mp="z2xjtspbrpxk">
                          {item.programLabel}
                        </span>
                        <span className="block text-xs text-gray-500 truncate" data-id="element-2580" data-mp="18tionxvldqox">
                          {item.degreeLabel} · {item.areaLabel}
                        </span>
                      </span>
                      <ArrowRightIcon className="w-4 h-4 flex-shrink-0 text-gray-400 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-navy" aria-hidden="true" data-id="element-2581" data-mp="g4l6durdrtw6" />
                    </Link>
                  </li>)}
              </ul>
              {comboResults.length > comboLimit && <button type="button" onClick={() => setComboLimit(n => n + 60)} className="mt-6 px-5 py-2.5 text-sm font-semibold text-navy border border-navy rounded-lg hover:bg-navy/5 transition-colors duration-200" data-id="element-2582" data-mp="21hy24fz2s0y5">
                  Show more
                </button>}
            </>}
        </div>
      </section>

      {/* Every URL this template serves, as plain paths. The sections above
          are for choosing a program; this is for seeing the full set. */}
      <section aria-labelledby="all-urls-heading" className="border-t border-gray-200" data-id="element-2583" data-mp="1z5vm8wyjra17">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-2584" data-mp="2rsglskp6esfu">
          <h2 id="all-urls-heading" className="text-2xl font-bold text-navy mb-2" data-id="element-2585" data-mp="2rukmktrs3bbw">
            All program information pages
          </h2>
          <p className="text-sm text-gray-600 max-w-2xl mb-6" data-id="element-2586" data-mp="2i2547vckkkqh">
            {allTemplatePages.length} pages served by{' '}
            <code className="text-xs bg-light px-1.5 py-0.5 rounded text-navy" data-id="element-2587" data-mp="35r6g5skm2e0i">
              /program-information/&#123;template&#125;
            </code>
            , plus {degreeProgramCombos.length.toLocaleString()} generated
            degree and program combinations listed above.
          </p>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1" data-id="element-2588" data-mp="2h0tinbqvk22r">
            {allTemplatePages.map(page => <li key={page.path} data-id="element-2589" data-mp="2c05tkvm7hvb6">
                <Link to={page.path} className="group flex items-baseline justify-between gap-3 py-2 border-b border-gray-100 focus:outline-none focus:ring-2 focus:ring-crimson rounded-sm" data-id="element-2590" data-mp="273xhxs77wk8r">
                  <span className="min-w-0" data-id="element-2591" data-mp="2zsuowz5zaum7">
                    <span className="block text-sm font-medium text-navy truncate group-hover:underline" data-id="element-2592" data-mp="2usqarfsjmbz5">
                      {page.label}
                    </span>
                    <code className="block text-[11px] text-gray-400 truncate" data-id="element-2593" data-mp="2pvzvguokbkes">
                      {page.path}
                    </code>
                  </span>
                  <ArrowRightIcon className="w-3.5 h-3.5 flex-shrink-0 self-center text-gray-300 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-navy" aria-hidden="true" data-id="element-2594" data-mp="1edyrbsddkf1z" />
                </Link>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="bg-navy" data-id="element-2595" data-mp="8lve5g0pafk3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 text-center" data-id="element-2596" data-mp="8mwtvxsnvq85">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" data-id="element-2597" data-mp="dk5c1ydt7u9e">
            Not sure which program fits?
          </h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7" data-id="element-2598" data-mp="ho4q8abmagzl">
            Answer a few questions about your goals and schedule and we'll
            connect you with accredited schools offering these programs. No
            cost, no obligation.
          </p>
          <Link to="/request-information" className="inline-flex items-center gap-2 bg-cta hover:bg-cta-dark text-white font-bold py-3.5 px-8 rounded-lg text-sm transition-colors duration-200" data-id="element-2599" data-mp="2oe1fwwgfzumf">
            Get Matched Now
            <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2600" data-mp="169bpa6m1yh59" />
          </Link>
        </div>
      </section>

      <LegalFooter data-id="element-2601" data-mp="1aan4rjb7plzq" />
    </main>;
}